import * as Select from '@radix-ui/react-select'
import type { Locale } from '@seaa/shared'
import { Check, CheckCircle2, CircleAlert, Globe2, LoaderCircle } from 'lucide-react'
import './language-menu.css'

export type MarketingLocale = Locale
export type LanguageMenuStatus = 'idle' | 'loading' | 'error' | 'success'
export type LanguageMenuPreviewState = 'hover' | 'focus' | 'active'

interface LanguageMenuProps {
  locale: MarketingLocale
  label: string
  onLocaleChange: (locale: MarketingLocale) => void
  disabled?: boolean
  status?: LanguageMenuStatus
  previewState?: LanguageMenuPreviewState
}

const languages: Array<{ value: MarketingLocale; code: string; label: string; lang: MarketingLocale }> = [
  { value: 'ar', code: 'AR', label: 'العربية', lang: 'ar' },
  { value: 'en', code: 'EN', label: 'English', lang: 'en' },
  { value: 'de', code: 'DE', label: 'Deutsch', lang: 'de' },
  { value: 'nl', code: 'NL', label: 'Nederlands', lang: 'nl' },
]

function StatusIcon({ status }: { status: LanguageMenuStatus }) {
  if (status === 'loading') return <LoaderCircle className="active-language-menu__spinner" aria-hidden="true" />
  if (status === 'error') return <CircleAlert aria-hidden="true" />
  if (status === 'success') return <CheckCircle2 aria-hidden="true" />
  return <Globe2 aria-hidden="true" />
}

export function LanguageMenu({
  locale,
  label,
  onLocaleChange,
  disabled = false,
  status = 'idle',
  previewState,
}: LanguageMenuProps) {
  const statusMessage = status === 'loading'
    ? locale === 'ar' ? 'جارٍ تغيير اللغة' : 'Changing language'
    : status === 'error'
      ? locale === 'ar' ? 'تعذّر تغيير اللغة' : 'Language change failed'
      : status === 'success'
        ? locale === 'ar' ? 'تغيّرت اللغة' : 'Language changed'
        : ''

  return <div className="active-language-menu">
    <Select.Root
      value={locale}
      onValueChange={(value) => onLocaleChange(value as MarketingLocale)}
      disabled={disabled}
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      <Select.Trigger
        className={`active-language-menu__trigger${previewState ? ` is-${previewState}` : ''}`}
        aria-label={label}
        aria-busy={status === 'loading' || undefined}
        aria-invalid={status === 'error' || undefined}
        data-status={status}
      >
        <StatusIcon status={status} />
        <span className="active-language-menu__value"><Select.Value /></span>
      </Select.Trigger>

      <Select.Portal>
        <Select.Content
          className="active-language-menu__content"
          position="popper"
          sideOffset={8}
          align="end"
          collisionPadding={10}
        >
          <Select.Viewport className="active-language-menu__viewport">
            {languages.map((language) => <Select.Item
              key={language.value}
              value={language.value}
              className="active-language-menu__item"
              lang={language.lang}
            >
              <span className="active-language-menu__code" aria-hidden="true">{language.code}</span>
              <Select.ItemText>{language.label}</Select.ItemText>
              <Select.ItemIndicator className="active-language-menu__indicator"><Check aria-hidden="true" /></Select.ItemIndicator>
            </Select.Item>)}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
    {statusMessage && <span className="sr-only" role={status === 'error' ? 'alert' : 'status'}>{statusMessage}</span>}
  </div>
}
